// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';

// Shared utils for Display Items.
import * as utils from './utils.jsx';

// Misc dependencies.
import { getLabel, UCFirst } from '../../utils/string.js';

/**
 * <pre>
 * Display Item > Reward related React components.
 *
 * </pre>
 *
 * @author Various
 * @namespace Common.Components.DisplayItems.Results
 */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Components */

/**
 * Leaderboard Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Results
 */
class Result extends React.Component {
  constructor(props) {
    super(props);
  }
  render() {
    const { item, itemOpts, templateOpts } = this.props;

    const profile = getPlayerProfile(item),
      points = getPlayerPoints(item),
      score = getPlayerScore(item),
      badges = getPlayerBadges(item);

    const innerStyle = {};

    let className = utils.getClassNamesForItem({ item, templateOpts, itemOpts });
    className += ' xTypeProfile';
    className += item.isFriend ? ' xProfileFriend' : '';

    return (
      <div className={className}>
        <div className="xItemInner" style={innerStyle}>
          <If condition={!!profile}>{profile}</If>
          <If condition={!!points}>{points}</If>
          <If condition={!!score}>{score}</If>
          <If condition={!!badges}>{badges}</If>
        </div>
      </div>
    );
  }
}

Result.propTypes = {
  item:         PropTypes.shape({}).isRequired,
  itemOpts:     PropTypes.shape({}),
  templateOpts: PropTypes.shape({}),
};

Result.defaultProps = {
  itemOpts:     {},
  templateOpts: {},
};

export { Result };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * Quiz results Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Results
 */
class Scoreresult extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const { assetUrl, assetUgcUrl, item, itemOpts, templateOpts } = this.props;
    const network = _.get(item, ['meta', 'network']);

    let _profile = utils.getItemProfile({ item }),
      _mainContent = utils.getItemContent({ item, type: 'main' }),
      _secondaryContent = utils.getItemContent({ item, type: 'secondary' }),
      _networkIcon = utils.getNetworkIcon(network),
      _timestamp = utils.getItemTimestamp({ item }),
      _itemAsset = utils.getItemAsset({ item, assetUrl, assetUgcUrl }),
      _sharingActions,
      _additionalActions;

    const sharing = _.get(item, ['actions', 'sharing']);
    if (!!sharing) {
      _sharingActions = utils.getSharingActions(item, itemOpts, _additionalActions, 'mode.overlay');
    }

    const innerStyle = {};

    const displayItemModel = {
      onClick: () => {
        this.props.onClick(item);
      },
    };

    if ('function' === typeof this.props.onClick) {
      displayItemModel.role = 'button';
    }

    const className = utils.getClassNamesForItem({ item, templateOpts, itemOpts });

    return (
      <div {...displayItemModel} className={className}>
        <div className="xItemInner" style={innerStyle}>
          {_networkIcon}
          {_itemAsset}
          {_mainContent}
          {_secondaryContent}
          {_timestamp}
          {_profile}
          {_sharingActions}
        </div>
      </div>
    );
  }
}

Scoreresult.propTypes = {
  item:         PropTypes.shape({}).isRequired,
  itemOpts:     PropTypes.shape({}),
  templateOpts: PropTypes.shape({}),
};

Scoreresult.defaultProps = {
  itemOpts:     {},
  templateOpts: {},
};

export { Scoreresult };

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private utility functions */

/**
 * Returns the wrapper leaderboard player's profile.
 *
 * @private
 *
 * @param {object} item - the player
 * @return {React} Wrapped player profile
 */
function getPlayerProfile(item) {
  const { avatar, badges, firstName = '', lastName = '', handle, levelImgSrc } = item;
  const username = formatUserName(firstName, lastName);

  return (
    <div className="xProfileContainer">
      <If condition={!!avatar}>
        <div className="xUserAvatar">
          <img src={avatar} alt={username || handle} />
        </div>
      </If>
      <div className="xUserDetails">
        <If condition={!!username}>
          <div className="xUserName">{username}</div>
        </If>
        <If condition={!!handle}>
          <div className="xUserHandle">{handle}</div>
        </If>
      </div>
      <If condition={!!levelImgSrc}>
        <div className="xMediaContainer xMediaImage xAssetLevelImage">
          <img src={levelImgSrc} alt={badges} className="xThumbnail" />
        </div>
      </If>
    </div>
  );
}

/**
 * Formats a users name.
 *
 * @private
 *
 * @param {string} firstName The users first name.
 * @param {string} lastName The users last name.
 * @returns {string}
 */
function formatUserName(firstName, lastName) {
  if (!firstName && !lastName) return '';

  if ((!firstName && !lastName) || typeof firstName === 'object' || typeof lastName === 'object') {
    return '';
  } else {
    // now you can do a simplistic replace on the tokens in the string

    // 	Taken from  grails-app/views/display/common/_i18n.gsp to avoid global var setup in tests.
    //  This is the default in that file.
    const defaultFormat = '{{firstname}} {{lastname}}';
    var repStr = getLabel('user.nameFormat', defaultFormat).toLowerCase();
    repStr = repStr.replace(new RegExp('{{firstname}}', 'gm'), UCFirst(firstName));
    repStr = repStr.replace(new RegExp('{{lastname}}', 'gm'), UCFirst(lastName));
  }

  return repStr;
}

/**
 * Returns the leaderboard player's loyalty points.
 *
 * @private
 *
 * @param {Object} item - the player
 * @return {React} wrapped loyalty points
 */
function getPlayerPoints(item) {
  const showPoints = _.get(item, ['itemOpts', 'showPoints']);
  const points = _.get(item, 'points');

  if (!!showPoints && !!points) {
    return (
      <div className="xCopyContainer xMediaText xScore xPointsTotal">
        <p className="xMeta xValue">{points}</p>
        <p className="xMeta xLabel">{getLabel('loyalty.points', 'Points')}</p>
      </div>
    );
  }
}

/**
 * Returns the leaderboard player's score.
 *
 * @private
 *
 * @param {Object} item - the player
 * @return {React} wrapped score
 */
function getPlayerScore(item) {
  const showScore = _.get(item, ['itemOpts', 'showScore']);
  const score = _.get(item, 'score');
  if (!!showScore && !!score) {
    return (
      <div className="xCopyContainer xMediaText xScore xScoreTotal">
        <p className="xMeta xValue">{score}</p>
        <p className="xMeta xLabel">{getLabel('leaderboard.score', 'Score')}</p>
      </div>
    );
  }
}

/**
 * Returns the leaderboard player's loyalty badges.
 *
 * @private
 *
 * @param {Object} item - the player
 * @return {React} wrapped loyalty badges
 */
function getPlayerBadges(item) {
  const showBadgeCount = _.get(item, ['itemOpts', 'showBadgeCount']);
  const badges = _.get(item, 'badges');

  if (!!showBadgeCount && !!badges) {
    return (
      <div className="xCopyContainer xMediaText xScore xBadgeCount">
        <p className="xMeta xValue">{badges}</p>
        <p className="xMeta xLabel">{getLabel('loyalty.badges', 'Badges')}</p>
      </div>
    );
  }
}
